import { getSharedServerSideProps } from '@src/utils/serverside/get-shared-serverside-props';
import { dataLayerConfig } from '@aspendental/shared-components-web';
import { Form, SchemaBuilder, TextInput, TextInputSchema, useHookForm } from '@aspendental/shared-forms-web';
import { CheckCircleOutline, Close, East } from '@mui/icons-material';
import { Box, CircularProgress, Modal, Typography, useMediaQuery, useTheme } from '@mui/material';
import LoginWithEmail from '@src/components/LoginWithEmail';
import PortalButton from '@src/components/PortalButton/PortalButton';
import { useEmailLogin } from '@src/services/useEmailLogin';
import { useAppStore } from '@src/store';
import { useState } from 'react';
import { patientServicesCloseButton, patientServicesContainer, patientServicesEmail, patientServicesSection, patientServicesSpinner, patientServicesTips, patientServicesTipsHeader, patientServicesTipsModal, patientServicesTipsModalButton, patientServicesTipsModalContent, patientServicesTipsModalNumber, patientServicesTipsModalSection } from './PatientServices.styles';
import { accountBreak } from './account-information/AccountInformation.styles';
export default function PatientServices() {
  const theme = useTheme();
  const [emailSent, setEmailSent] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    isPending,
    emailLoginMutation
  } = useEmailLogin();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const {
    enablePortalRedesignNoAuth
  } = useAppStore(store => store.officeRollout);
  const form = useHookForm({
    schema: SchemaBuilder([{
      name: 'email',
      validation: TextInputSchema.options.email
    }])
  });
  const submit = async () => {
    const email = form.watch('email');
    await emailLoginMutation({
      email: email
    });
    dataLayerConfig({
      event: 'patient_services_login',
      step_name: 'authentication_1',
      aspen_action: 'link_sent',
      auth_method: 'none'
    });
    setEmailSent(true);
  };
  return enablePortalRedesignNoAuth ? <>
			<Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} data-sentry-element="Modal" data-sentry-source-file="index.page.tsx">
				<Box sx={patientServicesTipsModal(isLargeScreen)} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
					<Box sx={patientServicesTipsHeader} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
						<Typography id="patient-services-tips-title" variant="headingS" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
							Try these tips if you didn&apos;t receive a link
						</Typography>
						<Box onClick={() => setIsModalOpen(false)} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
							<Close sx={patientServicesCloseButton} data-sentry-element="Close" data-sentry-source-file="index.page.tsx" />
						</Box>
					</Box>
					<Box sx={patientServicesTipsModalSection} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
						<Typography variant="bodyL" sx={patientServicesTipsModalNumber} data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
							1
						</Typography>
						<Box sx={patientServicesTipsModalContent} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
							<Typography variant="bodyLSemiBold" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
								Confirm you&apos;re using the correct email.
							</Typography>
							<Typography variant="bodyS" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
								Double-check you are using the correct email and it is the email associated with your
								account.
							</Typography>
						</Box>
					</Box>
					<Box sx={patientServicesTipsModalSection} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
						<Typography variant="bodyL" sx={patientServicesTipsModalNumber} data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
							2
						</Typography>
						<Box sx={patientServicesTipsModalContent} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
							<Typography variant="bodyLSemiBold" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">Check spam/junk folder.</Typography>
							<Typography variant="bodyS" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
								Check your span or junk folder in case the verification email was mistakenly categorized
								as spam.
							</Typography>
						</Box>
					</Box>
					<Box sx={patientServicesTipsModalSection} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
						<Typography variant="bodyL" sx={patientServicesTipsModalNumber} data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
							3
						</Typography>
						<Box sx={patientServicesTipsModalContent} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
							<Typography variant="bodyLSemiBold" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">Call your local office.</Typography>
							<Typography variant="bodyS" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
								Still having trouble? We recommend you call your local office for assistance.
							</Typography>
						</Box>
					</Box>
					<PortalButton variant="secondary" sx={patientServicesTipsModalButton} onClick={() => setIsModalOpen(false)} data-sentry-element="PortalButton" data-sentry-source-file="index.page.tsx">
						<Typography variant="bodySSemiBold" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">Dismiss</Typography>
					</PortalButton>
				</Box>
			</Modal>
			<Form form={form as any} onSubmit={submit} data-sentry-element="Form" data-sentry-source-file="index.page.tsx">
				<Box sx={patientServicesContainer} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
					<Typography variant="headingXL" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">Patient Services</Typography>
					<Typography variant="bodyL" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">
						Fill out your patient forms, manage your appointments, and pay your bills. All in one place!
					</Typography>
					<Box sx={patientServicesSection} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
						{emailSent ? <>
								<CheckCircleOutline sx={{
              fontSize: '50px',
              color: '#079455'
            }} />
								<Typography variant="headingS">We emailed you a link</Typography>
								<Typography variant="bodyM">
									Open your email and follow the link to your account
								</Typography>
								<PortalButton variant="secondary" type="submit" disabled={isPending}>
									{isPending && <CircularProgress size={24} sx={patientServicesSpinner('#282828')} />}
									<Typography variant="bodyMSemiBold" color="white">
										Resend link
									</Typography>
								</PortalButton>
								<Box sx={accountBreak} />
								<Typography variant="bodyM">Didn&apos;t get the link?</Typography>
								<PortalButton size="S" variant="tertiary" sx={patientServicesTips} onClick={() => setIsModalOpen(true)}>
									<Typography variant="bodySSemiBold">Try these tips</Typography>
									<East sx={{
                fontSize: 20,
                ml: '0.5rem'
              }} />
								</PortalButton>
							</> : <>
								<Typography variant="headingM">Sign in via email link</Typography>
								<Typography variant="bodyM">
									Enter your email and we&apos;ll send you a secure link to your account.
								</Typography>
								<TextInput required name="email" data-private label="Email address" sx={patientServicesEmail} enforceAlphaNumeric={false} placeholder="email@domain.com" />
								<PortalButton variant="primary" type="submit" disabled={isPending}>
									{isPending && <CircularProgress size={24} sx={patientServicesSpinner('white')} />}
									<Typography variant="bodyM" color="white">
										Send link
									</Typography>
								</PortalButton>
							</>}
					</Box>
					<Box sx={patientServicesSection} data-sentry-element="Box" data-sentry-source-file="index.page.tsx">
						<Typography variant="headingS" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">Pay as guest</Typography>
						<Typography variant="bodyM" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">View billing statements and make payments online.</Typography>
						<PortalButton variant="secondary" onClick={() => {
            globalThis.location.href = 'https://www.aspendental.com/pay';
          }} data-sentry-element="PortalButton" data-sentry-source-file="index.page.tsx">
							<Typography variant="bodyMSemiBold" data-sentry-element="Typography" data-sentry-source-file="index.page.tsx">Pay now</Typography>
						</PortalButton>
					</Box>
				</Box>
			</Form>
		</> : <LoginWithEmail data-sentry-element="LoginWithEmail" data-sentry-component="PatientServices" data-sentry-source-file="index.page.tsx" />;
}
export const getServerSideProps = async context => {
  return getSharedServerSideProps(context, {
    shouldFetchAppData: true
  });
};